import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { createAllDayEvent } from './recurrenceUtils';

export type EventType = 'personal' | 'family' | 'appointment' | 'birthday' | 'holiday';

export function getEventEmoji(eventType: EventType): string {
  switch (eventType) {
    case 'birthday':
      return '🎂';
    case 'appointment':
      return '📅';
    case 'family':
      return '👨‍👩‍👧‍👦';
    case 'holiday':
      return '🏖️';
    case 'personal':
    default:
      return '📝';
  }
}

export function getEventTypeLabel(eventType: EventType): string {
  switch (eventType) {
    case 'appointment':
      return 'Rendez-vous';
    case 'family':
      return 'Famille';
    case 'birthday':
      return 'Anniversaire';
    case 'holiday':
      return 'Vacances';
    case 'personal':
    default:
      return 'Personnel';
  }
}

export function createBirthdayEvent(person: { 
  id: string; 
  first_name: string; 
  last_name: string; 
  date_of_birth: string;
}, year: number = new Date().getFullYear()) {
  const birthDate = new Date(person.date_of_birth);
  const eventDate = new Date(year, birthDate.getMonth(), birthDate.getDate());
  
  // Use createAllDayEvent for proper all-day event structure
  const { start, end } = createAllDayEvent(eventDate);
  
  // Calculate age correctly - check if birthday has already occurred this year
  const today = new Date();
  let age = year - birthDate.getFullYear();
  
  // If we're calculating for current year and birthday hasn't occurred yet, subtract 1
  if (year === today.getFullYear()) {
    const thisYearBirthday = new Date(year, birthDate.getMonth(), birthDate.getDate());
    if (today < thisYearBirthday) {
      age--;
    }
  }
  
  return {
    title: `${person.first_name} ${person.last_name} (${age} ans)`,
    description: `Anniversaire de ${person.first_name} ${person.last_name}`,
    start_date: start.toISOString(),
    end_date: end.toISOString(),
    event_type: 'birthday' as const,
    is_recurring: true,
    recurrence_pattern: 'yearly',
    linked_persons: [person.id]
  };
}

export type EventFilter = 'all' | 'birthday' | 'appointment' | 'family' | 'personal' | 'holiday';

export function filterEventsByType(events: any[], filter: EventFilter) {
  if (filter === 'all') return events;
  return events.filter(event => event.event_type === filter);
}

export function getEventsForNext12Months(events: any[], filter: EventFilter) {
  const now = new Date();
  const next12Months = new Date();
  next12Months.setMonth(next12Months.getMonth() + 12);
  
  // Si on filtre sur "tous", garder le comportement actuel (tous les événements à venir)
  if (filter === 'all') {
    return events.filter(event => new Date(event.start_date) >= now);
  }
  
  // Pour un filtre spécifique, afficher tous les événements de ce type dans les 12 prochains mois
  const filteredEvents = events.filter(event => {
    const eventDate = new Date(event.start_date);
    return event.event_type === filter && 
           eventDate >= now && 
           eventDate <= next12Months;
  });
  
  return filteredEvents.sort((a, b) => 
    new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
  );
}

export const EVENT_FILTER_OPTIONS = [
  { value: 'all' as const, label: 'Tous', emoji: '' },
  { value: 'birthday' as const, label: 'Anniversaires', emoji: '🎂' },
  { value: 'appointment' as const, label: 'RDV', emoji: '📅' },
  { value: 'family' as const, label: 'Famille', emoji: '👨‍👩‍👧‍👦' },
  { value: 'personal' as const, label: 'Personnel', emoji: '📝' },
  { value: 'holiday' as const, label: 'Vacances', emoji: '🏖️' },
] as const;